import { useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { organizationMemberContract } from "@common/contracts/organization-member.contract";
import {
    OrganizationMember,
    OrganizationMemberDto,
} from "@common/dto/organization/organization-member.dto";
import { OrganizationInviteDto } from "@common/dto/organization/organization-invite.dto";
import { Organization } from "@common/types/organization";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { organizationQueryKeys } from "@api/organization.api";
import { organizationAuditLogQueryKeys } from "@api/organizationAuditLog.api";
import { keepPreviousDataWithinScope } from "@api/keepPreviousDataWithinScope";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient } from "../queryClient";

const organizationMemberContractClient = getBackendAPIClient(
    organizationMemberContract,
);

const organizationMemberQueryKeys = {
    all: () => ["organization-member"],
    organization: (organizationId: Organization["id"]) => [
        ...organizationMemberQueryKeys.all(),
        organizationId,
    ],
    invites: (organizationId: Organization["id"]) => [
        ...organizationMemberQueryKeys.organization(organizationId),
        "invites",
    ],
    clientUsers: (
        organizationId: Organization["id"],
        clientWorkspaceIds: string[] | undefined,
    ) => [
        ...organizationMemberQueryKeys.organization(organizationId),
        "client-users",
        clientWorkspaceIds,
    ],
} as const;

export function useCurrentOrganizationMembers({
    enabled = true,
    refetchOnMount,
}: {
    enabled?: boolean;
    /** React Query's `refetchOnMount`; false reuses cached members when a reader mounts, even stale ones. */
    refetchOnMount?: boolean;
} = {}) {
    const { organizationId } = useScope();

    return useQuery({
        ...(refetchOnMount !== undefined && { refetchOnMount }),
        queryKey: organizationId
            ? organizationMemberQueryKeys.organization(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }

            const result = await organizationMemberContractClient.getMembers({
                params: {
                    organizationId,
                },
            });

            return result.body.map(OrganizationMemberDto.deserialize);
        },
        enabled: enabled && !!organizationId,
    });
}

/** Users of the organization's clients who aren't firm members; `clientWorkspaceIds` narrows to those clients. Admin only. */
export function useOrganizationClientUsers({
    clientWorkspaceIds,
}: {
    clientWorkspaceIds?: string[];
} = {}) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? organizationMemberQueryKeys.clientUsers(
                  organizationId,
                  clientWorkspaceIds,
              )
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }

            const result =
                await organizationMemberContractClient.getClientUsers({
                    params: { organizationId },
                    query: { clientWorkspaceIds },
                });

            return result.body;
        },
        enabled: !!organizationId,
        placeholderData: keepPreviousDataWithinScope(organizationId ?? ""),
    });
}

export function usePendingOrganizationInvites() {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationId
            ? organizationMemberQueryKeys.invites(organizationId)
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }

            const result = await organizationMemberContractClient.getInvites({
                params: {
                    organizationId,
                },
            });

            return result.body.map(OrganizationInviteDto.deserialize);
        },
        enabled: !!organizationId,
    });
}

interface InviteOrganizationMemberPayload {
    emails: string[];
    role: OrganizationUserRole;
    workspaceAssignments: string[];
    assignToNewClients: boolean;
}

export function useInviteOrganizationMemberMutation(
    organizationId: Organization["id"],
) {
    return useMutation({
        mutationFn: async (payload: InviteOrganizationMemberPayload) =>
            await organizationMemberContractClient.inviteMember({
                params: {
                    organizationId,
                },
                body: {
                    ...payload,
                },
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey:
                    organizationMemberQueryKeys.organization(organizationId),
            });
        },
    });
}

export function useResendOrganizationInviteMutation(
    organization: Organization,
) {
    return useMutation({
        mutationFn: async (invite: { id: number }) => {
            await organizationMemberContractClient.resendInvite({
                params: {
                    organizationId: organization.id,
                    inviteId: invite.id,
                },
            });
        },
    });
}

export function useRemoveOrganizationMemberMutation(
    member: OrganizationMember,
    organization: Organization,
) {
    return useMutation({
        mutationFn: async () => {
            await organizationMemberContractClient.removeMember({
                params: {
                    organizationId: organization.id,
                    memberId: member.user.id,
                },
            });
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: organizationMemberQueryKeys.organization(
                        organization.id,
                    ),
                }),
                queryClient.invalidateQueries({
                    queryKey: organizationAuditLogQueryKeys.all(),
                }),
            ]);
        },
    });
}

export function useCancelOrganizationInviteMutation(
    organization: Organization,
) {
    return useMutation({
        mutationFn: async (invite: { id: number }) => {
            await organizationMemberContractClient.cancelInvite({
                params: {
                    organizationId: organization.id,
                    inviteId: invite.id,
                },
            });
        },
        onMutate: async (invite) => {
            await queryClient.setQueryData(
                organizationMemberQueryKeys.invites(organization.id),
                (data: OrganizationInviteDto[]) =>
                    data?.filter((i) => i.id !== invite.id) ?? [],
            );
        },
        onSuccess: async (_response) => {
            await queryClient.invalidateQueries({
                queryKey: organizationMemberQueryKeys.invites(organization.id),
            });
        },
    });
}

export function useUpdateOrganizationMemberPermissionsMutation(
    member: OrganizationMember,
    organization: Organization,
) {
    return useMutation({
        mutationFn: async (
            payload: ServerInferRequest<
                typeof organizationMemberContract.updateMemberPermissions
            >["body"],
        ) => {
            await organizationMemberContractClient.updateMemberPermissions({
                params: {
                    organizationId: organization.id,
                    memberId: member.user.id,
                },
                body: payload,
            });
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: organizationMemberQueryKeys.organization(
                        organization.id,
                    ),
                }),
                queryClient.invalidateQueries({
                    queryKey: organizationAuditLogQueryKeys.all(),
                }),
            ]);
        },
    });
}

export function useTransferOrganizationOwnerMutation(
    organizationId: Organization["id"],
) {
    return useMutation({
        mutationFn: async (memberId: number) => {
            await organizationMemberContractClient.transferOwner({
                params: { organizationId, memberId },
            });
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey:
                        organizationMemberQueryKeys.organization(
                            organizationId,
                        ),
                }),
                // The acting user's role is served by the separate organization
                // query (useActiveOrganizationRole → useOrganization); invalidate
                // it too so the just-demoted owner stops seeing owner-only UI.
                queryClient.invalidateQueries({
                    queryKey: organizationQueryKeys.all(),
                }),
            ]);
        },
    });
}
